import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { Calendar, ArrowLeft, Clock, ShieldCheck, CheckCircle2 } from 'lucide-react'
import { scrapedData } from '../data'
import { renderFormattedText } from '../utils/textFormatter'
import SEO from '../components/SEO'

export default function TreatmentDetail() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [treatment, setTreatment] = useState(null)
  const [otherTreatments, setOtherTreatments] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    const found = scrapedData.treatments.find((t) => t.slug === slug)
    if (found) {
      setTreatment(found)
      setOtherTreatments(scrapedData.treatments.filter((t) => t.slug !== slug).slice(0, 4))
    } else {
      setTreatment(null)
    }
    setLoading(false)
  }, [slug])

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center space-y-4">
        <div className="inline-block w-8 h-8 border-4 border-[#eebb53] border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-[#1E352F]/60">Indlæser behandling...</p>
      </div>
    )
  }

  if (!treatment) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center space-y-6">
        <SEO
          title="Behandling ikke fundet | Rørbækklinikken"
          description="Den efterspurgte behandling blev ikke fundet hos Rørbækklinikken i Viborg."
        />
        <h1 className="font-serif text-3xl font-bold text-[#1E352F]">Behandlingen blev ikke fundet</h1>
        <p className="text-sm text-[#1E352F]/70 max-w-md mx-auto">
          Beklager, den behandlingsform du leder efter, findes ikke i vores system eller er blevet flyttet.
        </p>
        <Link
          to="/behandlinger"
          className="inline-flex items-center gap-2 bg-[#1E352F] hover:bg-[#2D4A43] text-white px-6 py-2.5 rounded-full text-sm font-semibold transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Se alle behandlinger
        </Link>
      </div>
    )
  }

  // Helper to check if description is already part of the content to prevent duplication
  const cleanString = (str) => {
    if (!str) return ''
    return str
      .replace(/[\s\u00a0]+/g, ' ')
      .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()?"']/g, '')
      .trim()
      .toLowerCase()
  }

  const contentClean = cleanString(treatment.content)
  const descClean = cleanString(treatment.description)
  const isDescInContent = descClean && contentClean.includes(descClean)

  const metaTitle = `${treatment.title} i Viborg | Rørbækklinikken`
  const metaDescription = `${treatment.title} hos Rørbækklinikken i Viborg. ${treatment.description || ''}`
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 160)

  return (
    <div className="space-y-16 pb-16">
      <SEO
        title={metaTitle}
        description={metaDescription}
        ogImage={treatment.image}
      />
      
      {/* Breadcrumb & Back Link */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <Link
          to="/behandlinger"
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#eebb53] hover:text-[#dca63d] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Tilbage til alle behandlinger
        </Link>
      </div>

      {/* Main Treatment Presentation */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Left Column: Image & Quick Info */}
          <div className="lg:col-span-5 lg:sticky lg:top-24 space-y-6">
            <div className="relative rounded-2xl overflow-hidden shadow-md border border-[#E6DFD3] aspect-[4/3] sm:aspect-[16/11]">
              <img
                src={treatment.image}
                alt={treatment.title}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Booking Card */}
            <div className="bg-[#F5F0E6] rounded-xl p-6 border border-[#E6DFD3] space-y-4">
              <h3 className="font-serif text-lg font-bold text-[#1E352F] border-b border-[#E6DFD3] pb-2">
                Bestil tid online
              </h3>
              <p className="text-xs sm:text-sm text-[#1E352F]/70 leading-relaxed">
                Book din næste behandling nemt og hurtigt via vores Planway online booking.
              </p>
              <div className="pt-2">
                <a
                  href="https://roerbaekklinikken.planway.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-[#eebb53] hover:bg-[#dca63d] text-white py-3 px-6 rounded-full text-sm font-semibold transition-colors flex items-center justify-center gap-2 shadow-sm"
                >
                  <Calendar className="w-4 h-4" />
                  Gå til online booking
                </a>
              </div>
            </div>

            {/* Quality Guarantee */}
            <div className="bg-white rounded-xl p-6 border border-[#E6DFD3] space-y-3">
              <h4 className="font-serif text-sm font-bold text-[#1E352F] flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-[#eebb53]" />
                Professionel garanti
              </h4>
              <p className="text-xs text-[#1E352F]/70 leading-relaxed">
                Alle vores behandlinger udføres af erfarne, autoriserede terapeuter med dybdegående anatomisk viden og fokus på din sikkerhed og tryghed.
              </p>
            </div>
          </div>

          {/* Right Column: Detailed Text Content */}
          <div className="lg:col-span-7 space-y-6">
            <div className="space-y-2">
              <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1E352F]">
                {treatment.title}
              </h1>
              {!isDescInContent && (
                <p className="text-base sm:text-lg text-[#eebb53] font-medium italic">
                  {treatment.description}
                </p>
              )}
            </div>

            {/* Verbatim Content */}
            <div className="text-sm sm:text-base text-[#1E352F]/80 leading-relaxed space-y-4">
              {renderFormattedText(treatment.content)}
            </div>
          </div>

        </div>
      </section>

      {/* Presenting Therapists (Verbatim from Scraped Treatment Pages) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 border-t border-[#E6DFD3] pt-16 space-y-12">
        <div className="text-center max-w-3xl mx-auto space-y-3">
          <h2 className="font-serif text-3xl font-bold text-[#1E352F]">
            Behandlere tilknyttet denne behandlingsform
          </h2>
          <p className="text-sm sm:text-base text-[#1E352F]/70">
            Du kan møde følgende erfarne behandlere på klinikken, der alle arbejder med det hele menneske som fælles udgangspunkt.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {scrapedData.therapists.map((therapist, idx) => (
            <div
              key={therapist.name}
              className={`bg-white rounded-2xl border border-[#E6DFD3] p-6 sm:p-8 shadow-sm flex flex-col items-center text-center space-y-4 ${
                idx === 2 ? 'md:col-span-12 lg:col-span-4' : 'md:col-span-6 lg:col-span-4'
              }`}
            >
              <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-[#F5F0E6] shadow-sm">
                <img
                  src={therapist.image}
                  alt={therapist.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="space-y-1">
                <h3 className="font-serif text-lg font-bold text-[#1E352F]">
                  {therapist.name}
                </h3>
                <p className="text-xs font-semibold text-[#eebb53] uppercase tracking-wider">
                  {therapist.role}
                </p>
              </div>
              <div className="w-full pt-4 border-t border-[#F5F0E6] space-y-2">
                <p className="text-xs font-bold text-[#1E352F]/60 uppercase tracking-wider">Specialer:</p>
                <div className="flex flex-wrap justify-center gap-1.5">
                  {therapist.specialties.slice(0, 4).map((spec) => (
                    <span
                      key={spec}
                      className="text-[10px] font-medium bg-[#F5F0E6]/60 text-[#1E352F]/80 px-2 py-0.5 rounded-full"
                    >
                      {spec}
                    </span>
                  ))}
                  {therapist.specialties.length > 4 && (
                    <span className="text-[10px] font-bold text-[#eebb53] px-2 py-0.5">
                      +{therapist.specialties.length - 4} mere
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Other Treatments Section */}
      <section className="bg-[#FDFBF7] py-16 border-y border-[#E6DFD3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          <h2 className="font-serif text-2xl font-bold text-[#1E352F] text-center">
            Andre behandlingsformer
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {otherTreatments.map((t) => (
              <div
                key={t.slug}
                className="bg-white rounded-xl border border-[#E6DFD3] overflow-hidden flex flex-col justify-between shadow-sm"
              >
                <div className="p-5 space-y-3">
                  <h3 className="font-serif text-lg font-bold text-[#1E352F] line-clamp-1">
                    {t.title}
                  </h3>
                  <p className="text-xs text-[#1E352F]/70 line-clamp-3 leading-relaxed">
                    {t.description}
                  </p>
                </div>
                <div className="px-5 pb-5 pt-2 flex items-center justify-between border-t border-[#F5F0E6]">
                  <Link
                    to={`/behandlinger/${t.slug}`}
                    className="text-xs font-bold text-[#eebb53] hover:text-[#dca63d] transition-colors"
                  >
                    Læs mere
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  )
}
